import {
    collection,
    component,
    EntityData,
    FedeltaImageData,
    field,
    ImageUtils,
    localDateField,
    SearchModel,
    SearchResult,
    SearchSortField,
    timeField,
    transientField
} from "@fedelta-pos/data-client";
import {computed, Inject, inject, Injectable, InjectionToken, Injector, Optional, Signal, signal, WritableSignal} from "@angular/core";
import {Observable} from "rxjs";
import {FilterProvider} from "../configuration/filter.provider";
import {IntegratedPaymentProvider} from '../payment/payment';
import {OrderType} from '../order/order';

export const STORE_SEARCH_FILTER_PROVIDER: InjectionToken<FilterProvider> = new InjectionToken<FilterProvider>("StoreSearchFilterProvider");

export enum StoreDisplayStatus {
    OFFLINE,
    CURRENTLY_CLOSED,
    CLOSED_AT_TIME,
    CURRENTLY_OPEN,
    OPEN_AT_TIME
}

// noinspection JSUnusedGlobalSymbols
export enum StoreOpeningHoursSpecifier {
    DAY_OF_WEEK = "DAY_OF_WEEK",
    DATE = "DATE",
    DATE_SCHEDULE = "DATE_SCHEDULE",
    TIME_SCHEDULE = "TIME_SCHEDULE"
}

export class HoursData extends EntityData<HoursData> {
    @timeField("hh:mm a") start: Date;
    @timeField("hh:mm a") end: Date;
}

export class DayHoursData extends EntityData<HoursData> {
    @collection(HoursData) hours: HoursData[];
    @field closedForTheDay: boolean;
}

export class StandardHours extends EntityData<StandardHours> {
    @component monday: DayHoursData;
    @component tuesday: DayHoursData;
    @component wednesday: DayHoursData;
    @component thursday: DayHoursData;
    @component friday: DayHoursData;
    @component saturday: DayHoursData;
    @component sunday: DayHoursData;
}

export class NonStandardHours extends EntityData<NonStandardHours> {
    @field specifier: StoreOpeningHoursSpecifier;
    @localDateField specificLocalDate: Date;
    @field schedule: string;
    @component hours: DayHoursData;
}

export class StoreOpeningHoursData extends EntityData<StoreOpeningHoursData> {
    @component standardHours: StandardHours;
    @collection(NonStandardHours) nonStandardHours: NonStandardHours[];
}

export class TableData extends EntityData<TableData> {
    @field tableLookupCode: string;
    @field tableNumber: string;

    compareTable(other: TableData): number {
        const p1: TableNumberParts = new TableNumberParts(this.tableLookupCode);
        const p2: TableNumberParts = new TableNumberParts(other.tableLookupCode);

        // Compare prefixes
        const prefixComp = p1.prefix.localeCompare(p2.prefix);
        if (prefixComp !== 0) {
            return prefixComp;
        }

        // Compare numbers
        const numberComp = p1.number - p2.number;
        if (numberComp !== 0) {
            return numberComp;
        }

        // Compare suffixes
        return p1.suffix.localeCompare(p2.suffix);
    }
}

/**
 * Table number components that can be used for sorting table numbers into a logical order
 */
export class TableNumberParts {
    prefix: string;
    number: number = Number.MAX_SAFE_INTEGER;
    suffix: string;

    constructor(table: string) {
        let prefixBuilder: string[] = [];
        let suffixBuilder: string[] = [];

        let numberFound = false;
        let numberEnd = false;
        let numberBuilder: string[] = [];

        for (let i = 0; i < table.length; i++) {
            const c = table.charAt(i);

            if (/\d/.test(c)) {
                if (!numberEnd) {
                    numberBuilder.push(c);
                    numberFound = true;
                } else {
                    suffixBuilder.push(c);
                }
            } else {
                if (!numberFound) {
                    prefixBuilder.push(c);
                } else {
                    numberEnd = true;
                    suffixBuilder.push(c);
                }
            }
        }

        this.prefix = prefixBuilder.join("");
        if (numberFound) {
            const num = parseInt(numberBuilder.join(""), 10);
            if (!isNaN(num)) {
                this.number = num;
            }
        }
        this.suffix = suffixBuilder.join("");
    }
}

export class StoreData extends EntityData<StoreData> {
    @field storeId: number;
    @field storeName: string;
    @field collectiveStoreName: string;
    @field description: string;
    @field storeGroup: string;
    @field outletId: number;
    @field locationId: number;
    @field locationName: string;
    @field venueId: string;
    @field externalAppURL: string;
    @field externalStoreId: number;
    @field parentAppStore: boolean;

    @field city: string;
    @field postcode: string;
    @field state: string;
    @field street: string;
    @field telephone: string;
    @field latitude: number;
    @field longitude: number;
    @field timeZone: string;

    @field menuId: number;
    @field paymentProvider: IntegratedPaymentProvider;

    @field completionTime: number;
    @field currentWaitTimeMinutes: number;

    @component storeOpeningHours: StoreOpeningHoursData;
    @field closed: boolean;
    @field orderLocalDateTime: string; // Date/time local to the store in the format of yyyy-MM-ddTHH:mm:ss
    @field closedAtOrderDateTime: boolean;
    @field systemStartLocalDateTime: string; // Date/time local to the store in the format of yyyy-MM-ddTHH:mm:ss - corresponds to system start time
    @field systemEndLocalDateTime: string;   // Date/time local to the store in the format of yyyy-MM-ddTHH:mm:ss - corresponds to system end time

    @field available: boolean;
    @field offline: boolean;

    @field orderAtTableStore: boolean;
    @field orderAheadStore: boolean;
    @field orderAheadLaterTimeStore: boolean = true;
    @field orderAheadLaterStore: boolean;
    @field orderDeliveryStore: boolean;
    @field maxOrderLaterDays: number;
    @field minPickupOrderAmount: number;
    @field maxPickupOrderAmount: number;
    @field minDeliveryOrderAmount: number;
    @field maxDeliveryOrderAmount: number;
    @field minTableOrderAmount: number;
    @field maxTableOrderAmount: number;
    @field rsaMinimumAge: number;
    @field allowTableGroupOrders: boolean = true;
    @field allowPickupGroupOrders: boolean = false;
    @field sendTableOrderReady: boolean = false;
    @field integratedDelivery: boolean;

    @field allowTips: boolean = false;
    @field highlightTips: boolean = false;
    @field tipInstructions: string;
    @field tipPercentage1: number;
    @field tipPercentage2: number;
    @field tipPercentage3: number;
    @field tipPercentage4: number;

    @field allowOrderItemNotes: boolean = false;

    @component image: FedeltaImageData;
    @field imageOpacity: number;
    @component locationImage: FedeltaImageData;
    @field horizontalLogoImage: string;
    @field horizontalLogoHeightPx: number;
    @field verticalLogoImage: string;
    @field verticalLogoWidthPx: number;

    @collection(TableData) availableTables: TableData[];

    @component table: TableData;

    /**
     * Calculated distance from a specified location
     */
    @transientField distance: number;

    @transientField _horizontalLogoImageSvg: string;
    @transientField _verticalLogoImageSvg: string;

    get horizontalLogoImageSvg(): string {
        if (this._horizontalLogoImageSvg === undefined) {
            this._horizontalLogoImageSvg = ImageUtils.base64ToSvg(this.horizontalLogoImage) || null;
        }
        return this._horizontalLogoImageSvg || undefined;
    }

    get verticalLogoImageSvg(): string {
        if (this._verticalLogoImageSvg === undefined) {
            this._verticalLogoImageSvg = ImageUtils.base64ToSvg(this.verticalLogoImage) || null;
        }
        return this._verticalLogoImageSvg || undefined;
    }

    canOrder(orderType: OrderType): boolean {
        return (orderType === OrderType.ORDER_AHEAD && this.orderAheadStore) ||
            (orderType === OrderType.ORDER_DELIVERY && this.orderDeliveryStore) ||
            (orderType === OrderType.ORDER_AT_TABLE && this.orderAtTableStore);
    }

    getDisplayStatus(orderType: OrderType): StoreDisplayStatus {
        return orderType === OrderType.ORDER_AT_TABLE ? (this.closed ? StoreDisplayStatus.CURRENTLY_CLOSED : this.offline ? StoreDisplayStatus.OFFLINE : StoreDisplayStatus.CURRENTLY_OPEN)
            : this.closedAtOrderDateTime ? (this.orderLocalDateTime ? StoreDisplayStatus.CLOSED_AT_TIME : StoreDisplayStatus.CURRENTLY_CLOSED)
                : this.offline ? StoreDisplayStatus.OFFLINE
                    : (this.orderLocalDateTime ? StoreDisplayStatus.OPEN_AT_TIME : StoreDisplayStatus.CURRENTLY_OPEN);
    }
}

@Injectable()
export class StoreSearchModel extends SearchModel<StoreData> {
    public static readonly FILTER_TABLE_LOOKUP_CODE = "tableLookupCode";
    public static readonly FILTER_ORDER_LOCAL_DATE_TIME = "orderLocalDateTime";
    public static readonly FILTER_SINGLE_STORE_ID = "singleStoreId";
    public static readonly FILTER_STORE_GROUP = "storeGroup";
    public static readonly FILTER_STORES = "stores";
    public static readonly FILTER_ORDER_AHEAD_ONLY = "orderAheadOnly";
    public static readonly FILTER_ORDER_DELIVERY_ONLY = "orderDeliveryOnly";
    public static readonly FILTER_ORDER_AT_TABLE_ONLY = "orderAtTableOnly";
    public static readonly FILTER_DISABLE_OTHER_ORDER_TYPES = "disableOtherOrderTypes";
    public static readonly FILTER_LOCAL_APP_URL = "localAppURL";
    public static readonly FILTER_PARENT_APP_URL = "parentAppURL";
    public static readonly FILTER_LOCATION_ID = "locationId";

    public nearestStores: WritableSignal<StoreData[]> = signal([]);

    public readonly hasOrderAheadStores: Signal<boolean> = computed<boolean>(() => this.dataSignal()?.some((store: StoreData) => store.orderAheadStore));
    public readonly hasOrderDeliveryStores: Signal<boolean> = computed<boolean>(() => this.dataSignal()?.some((store: StoreData) => store.orderDeliveryStore));
    public readonly hasOrderAtTableStores: Signal<boolean> = computed<boolean>(() => this.dataSignal()?.some((store: StoreData) => store.orderAtTableStore));
    public readonly hasMultipleStoreTypes: Signal<boolean> = computed<boolean>(() => ((this.hasOrderAheadStores() ? 1 : 0) + (this.hasOrderDeliveryStores() ? 1 : 0) + (this.hasOrderAtTableStores() ? 1 : 0)) > 1);

    constructor(@Optional() @Inject(STORE_SEARCH_FILTER_PROVIDER) private filterProvider: FilterProvider) {
        console.log("--- StoreSearchModel");
        super(inject(Injector), StoreData);
        this.sort = [new SearchSortField("state", "asc"), new SearchSortField("collectiveStoreName", "asc")];
    }


    /**
     * Finds the nearest stores from specified coordinates. The search should be called before calling this method
     * Stores are ordered by distance.
     * Only stores within StoreSearchModel.MAX_DISTANCE are included.
     *
     * @param storeLimit
     * @param storeRange
     * @param deliveryStoreRange
     * @param latitude
     * @param longitude
     */
    public async loadNearestStores(storeLimit: number, storeRange: number, deliveryStoreRange: number, latitude?: number, longitude?: number): Promise<void> {
        this.nearestStores.set([]);
        if (latitude && longitude) {
            this.findNearestStores(storeLimit, storeRange, deliveryStoreRange, latitude, longitude);
        }
    }

    /**
     * Finds the nearest stores from specified coordinates. The search should be called before calling this method.
     * Stores are ordered by distance.
     * Only stores within StoreSearchModel.MAX_DISTANCE are included.
     *
     * If the stores have not yet been loaded, an empty array will be returned.
     *
     * For waiters, the search only returns stores for the device's registered location. These are sorted by name.
     *
     * @param storeLimit
     * @param storeRange
     * @param deliveryStoreRange
     * @param latitude
     * @param longitude
     * @param deliveryOnly
     */
    public findNearestStores(storeLimit: number, storeRange: number, deliveryStoreRange: number, latitude: number, longitude: number, deliveryOnly: boolean = false): void {
        this.nearestStores.set(StoreSearchModel.applyNearestStoreLimit(storeLimit, this.data
            .filter((store: StoreData) => store.latitude && store.longitude)
            .filter((store: StoreData) => !deliveryOnly || store.orderDeliveryStore)
            .map((store: StoreData) => {
                store.distance = this.distance(latitude, longitude, store.latitude, store.longitude, "K", 2);
                return store;
            })
            .filter((store: StoreData) => {
                if (deliveryStoreRange && store.orderDeliveryStore && (deliveryOnly || (!store.orderAheadStore && !store.orderAtTableStore))) {
                    return store.distance < deliveryStoreRange;
                } else if (storeRange && !deliveryOnly) {
                    return store.distance < storeRange;
                }
                return true;
            })));
    }

    public static applyNearestStoreLimit(storeLimit: number, stores: StoreData[]): StoreData[] {

        // Sort stores by distance
        stores.sort((a: StoreData, b: StoreData) => a.distance - b.distance);

        // Get unique location ids (up to the specified store limit)
        let locationIds: number[] = stores.map((store: StoreData) => store.locationId);
        locationIds = locationIds.filter((locationId: number, pos: number) => locationIds.indexOf(locationId) === pos).slice(0, storeLimit);

        // Return the stores at the selected locations
        return stores.filter((store: StoreData) => locationIds.indexOf(store.locationId) >= 0);
    }

    public clearNearestStores(): void {
        this.nearestStores.set([]);
    }

    /**
     * Calculate the distance between two points
     * @param lat1
     * @param lon1
     * @param lat2
     * @param lon2
     * @param unit
     * @param sigDigits
     * @returns {number}
     */
    private distance(lat1: number, lon1: number, lat2: number, lon2: number, unit: string, sigDigits: number): number {
        let radlat1: number = Math.PI * lat1 / 180;
        let radlat2: number = Math.PI * lat2 / 180;
        let theta: number = lon1 - lon2;
        let radtheta: number = Math.PI * theta / 180;
        let dist: number = Math.sin(radlat1) * Math.sin(radlat2) + Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
        dist = Math.acos(dist);
        dist = dist * 180 / Math.PI;
        dist = dist * 60 * 1.1515;
        if (unit === "K") {
            dist = dist * 1.609344;
        }
        if (unit === "M") {
            dist = dist * 0.8684;
        }
        let multiplier: number = Math.pow(10, sigDigits - Math.floor(Math.log(dist) / Math.LN10) - 1);
        return Math.round(dist * multiplier) / multiplier;
    }

    override searchRecords(filter: any, start: number, limit: number, sort: SearchSortField[]): Observable<SearchResult> {
        let currentFilter: any = Object.assign({}, filter);
        if (this.filterProvider) {
            const interceptFilters: any = this.filterProvider.getFilters(currentFilter);
            if (interceptFilters) {
                for (let key in interceptFilters) {
                    currentFilter[key] = interceptFilters[key];
                }
            }
        }
        return super.searchRecords(currentFilter, start, limit, sort);
    }
}
